import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { securityApi } from '../../services/api'
import { toast } from 'sonner'

// ============================================
// SECURITY TYPES
// ============================================

export interface LoginAttempt {
  id: string
  device_name: string
  event_type: 'login_success' | 'login_failure'
  event_type_display: string
  ip_address: string
  user_agent: string
  timestamp: string
  failure_reason: string
}

export interface Device {
  id: string
  token_key: string
  device_name: string
  platform: string
  browser: string
  os: string
  ip_address: string
  created: string
  expiry: string
  last_active_at: string
  is_current: boolean
}

export interface RevokeAllResponse {
  revoked_count: number
}

// ============================================
// SECURITY QUERIES
// ============================================

/**
 * Hook for fetching login attempts history
 */
export function useLoginAttempts() {
  return useQuery({
    queryKey: queryKeys.security.loginAttempts,
    queryFn: () => securityApi.getLoginAttempts(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook for fetching active devices/sessions
 */
export function useDevices() {
  return useQuery({
    queryKey: queryKeys.security.devices,
    queryFn: () => securityApi.getDevices(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// ============================================
// SECURITY MUTATIONS
// ============================================

/**
 * Hook for revoking a specific device/session
 */
export function useRevokeDevice() {
  return useMutation({
    mutationFn: (deviceId: string) => securityApi.revokeDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.security.devices })
      toast.success('Device revoked successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for revoking all devices/sessions except current
 */
export function useRevokeAllDevices() {
  return useMutation({
    mutationFn: () => securityApi.revokeAllDevices(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.security.devices })
      toast.success(`${data.revoked_count} device(s) revoked successfully`)
    },
    onError: (error: any) => {

    },
  })
}
